import { BandLayer, ChartFrame, LineLayer, ZeroLine } from '@finsim/charts';
import type { Scenario } from '@finsim/model';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { McMetric } from '../../../entities/simulation';
import { cn } from '../../../shared/lib/cn';
import { scenarioColor } from '../lib/series';
import { useFans } from './fan-table';
import { summarize, type CanvasData } from './use-canvas';

/** Opacity of the outer (P5–P95) and inner (P10–P90) bands. */
const OUTER = 0.12;
const INNER = 0.24;

export interface FanChartProps {
  data: CanvasData;
  title: string;
  metric: McMetric;
  scenarios: readonly Scenario[];
  height: number;
  hover: number;
  onHover: (i: number) => void;
}

/**
 * Monte Carlo range of a headline series: per variant, a light P5–P95 band, a darker P10–P90
 * band and the median line, in the variant's colour.
 */
export function FanChart({
  data,
  title,
  metric,
  scenarios,
  height,
  hover,
  onHover,
}: FanChartProps) {
  const { t } = useTranslation();
  const { dates, range, fmt } = data;
  const fans = useFans(data, metric, scenarios);
  const domainSeries = useMemo(() => fans.flatMap((f) => [f.bands.p5, f.bands.p95]), [fans]);

  return (
    <ChartFrame
      markers={data.markers}
      dates={dates}
      range={range}
      height={height}
      domainSeries={domainSeries}
      formatY={fmt.compact}
      formatXTick={fmt.year}
      ariaLabel={summarize(
        title,
        fans.map((f) => ({ label: `${f.scenario.name} (${t('mc.median')})`, values: f.bands.p50 })),
        range,
        fmt.compact,
      )}
      hoverIndex={hover}
      onHoverChange={onHover}
      renderTooltip={(i) => (
        <div className="space-y-1.5">
          <p className="text-text-3">{dates[i] ? fmt.monthLong(dates[i]) : ''}</p>
          {fans.map(({ scenario, bands }) => (
            <div key={scenario.id} className="space-y-0.5">
              <p className="flex items-center justify-between gap-4">
                <span className="flex items-center gap-1.5 text-text-2">
                  <span
                    aria-hidden
                    className="h-0.5 w-2.5 rounded-full"
                    style={{ background: scenarioColor(scenario) }}
                  />
                  {fans.length > 1 ? scenario.name : t('mc.median')}
                </span>
                <span
                  className={cn('tabular font-medium', (bands.p50[i] ?? 0) < 0 && 'text-negative')}
                >
                  {fmt.money(bands.p50[i] ?? 0)}
                </span>
              </p>
              <p className="flex justify-between gap-4 text-text-3">
                <span>{t('mc.inner')}</span>
                <span className="tabular">
                  {t('mc.range', {
                    from: fmt.compact(bands.p10[i] ?? 0),
                    to: fmt.compact(bands.p90[i] ?? 0),
                  })}
                </span>
              </p>
              <p className="flex justify-between gap-4 text-text-3">
                <span>{t('mc.outer')}</span>
                <span className="tabular">
                  {t('mc.range', {
                    from: fmt.compact(bands.p5[i] ?? 0),
                    to: fmt.compact(bands.p95[i] ?? 0),
                  })}
                </span>
              </p>
            </div>
          ))}
        </div>
      )}
    >
      {fans.map(({ scenario, bands }) => (
        <g key={scenario.id}>
          <BandLayer
            lower={bands.p5}
            upper={bands.p95}
            color={scenarioColor(scenario)}
            opacity={OUTER}
          />
          <BandLayer
            lower={bands.p10}
            upper={bands.p90}
            color={scenarioColor(scenario)}
            opacity={INNER}
          />
        </g>
      ))}
      <ZeroLine />
      {fans.map(({ scenario, bands }) => (
        <LineLayer key={scenario.id} values={bands.p50} color={scenarioColor(scenario)} />
      ))}
    </ChartFrame>
  );
}
